import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import { GACompleteOrderFormErrors } from '@analytics/classes/ga.complete.class';
import { AnalyticsService } from '@analytics/services/analytics.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DirectDebitHelper } from '@complete-order/classes/direct.debit.helper.class';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { FormService } from '@complete-order/services/form-service.service';
import { BaseComponent } from '@core/components/base/base.component';
import {
  CurrentPlan,
  DirectDebitControls,
  DirectDebitErrors,
  DirectDebitFormState,
  ErrorType,
  fadeInOut,
  FormStep,
  Offers,
  slide,
  slideInOut,
} from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { getPdfPaths } from '@shared/helpers/get-pdf-paths';
import { debounceTime, takeUntil, withLatestFrom } from 'rxjs/operators';

export enum Mode {
  EDIT = 'edit',
  READONLY = 'readonly',
}

@Component({
  selector: 'app-direct-debit',
  templateUrl: './direct-debit.component.html',
  styleUrls: ['./direct-debit.component.scss'],
  animations: [fadeInOut, slideInOut, slide],
})
export class DirectDebitComponent extends BaseComponent implements OnChanges {
  @Output() continueClick: EventEmitter<FormStep> = new EventEmitter();
  @Output() createOrderClick: EventEmitter<null> = new EventEmitter();

  @Input() form: UntypedFormGroup;
  @Input() currentPlan: CurrentPlan;
  @Input() replacementCost: number;
  @Input() offers: Offers;
  @Input() directDebitReuse: boolean;
  @Input() directDebitAutofillAvailable: boolean;
  @Input() directDebitFormState: DirectDebitFormState;
  @Input() optimizely = false;

  @ViewChild('accountHolder') accountHolder: ElementRef;

  directDebitFormState$ = this.completeOrderService.directDebitFormState$;
  DirectDebitFormState = DirectDebitFormState;
  DirectDebitControls = DirectDebitControls;

  directDebitMode = Mode.EDIT;
  previousDirectDebitState = null;
  isFocusDirectDebitFieldsTriggered = false;
  isDirectDebitFormUpdated = false;

  DirectDebitErrors = DirectDebitErrors;
  FormStep = FormStep;
  ErrorType = ErrorType;
  Mode = Mode;

  confirmTermsAndConditionsRead = false;
  showReadTermsAndConditionError = false;
  pdfFolderPath!: {
    termsAndConditions: string;
    ipid: string;
  };

  /**
   * Link to the Direct Debit Guarantee page that is configured in config.json
   */
  ddGuaranteeLink = this.buildConfigService.config?.dd_guarantee;

  /**
   * Populate dropdown days for a month
   */
  days = Array(28)
    .fill(0)
    .map((day, i) => (i + 1).toString().padStart(2, '0'));

  constructor(
    public readonly completeOrderService: CompleteOrderService,
    private readonly additionalServicesService: AdditionalServicesService,
    private readonly formService: FormService,
    private readonly analyticsService: AnalyticsService,
    private readonly buildConfigService: BuildConfigService
  ) {
    super();
  }

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    if (
      this.previousDirectDebitState &&
      !this.isDirectDebitFormUpdated &&
      this.directDebitAutofillAvailable
    ) {
      this.form
        .get(FormStep.DirectDebit)
        .patchValue(this.previousDirectDebitState);
    }
  }

  setDocumentLink(schemeCode = this.offers?.current_plan?.scheme_code): void {
    this.pdfFolderPath = getPdfPaths(schemeCode);
  }

  /**
   * Setup direct debit form
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.form) {
      if (this.directDebitReuse) {
        this.loadDirectDebitData();
      }

      this.watchDirectDebit();
      this.validateDirectDebit();
    }

    if (changes.directDebitAutofillAvailable) {
      this.directDebitMode = this.directDebitAutofillAvailable
        ? Mode.READONLY
        : Mode.EDIT;
    }

    if (changes.offers) {
      this.setDocumentLink();
    }
  }

  /**
   * Watch changes to the direct debit form to reset dd form state functionality
   */
  watchDirectDebit() {
    this.form
      .get(FormStep.DirectDebit)
      .valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.completeOrderService.resetDirectDebitValidation();
        this.completeOrderService.resetCreateOrder();
      });
  }

  /**
   * Autofill direct debit data
   */
  loadDirectDebitData() {
    const { dd_details } = this.currentPlan;
    const { account_number, sort_code, name, nom_pay_day } = dd_details;

    const nomPayDay = nom_pay_day
      ? nom_pay_day.toString().padStart(2, '0')
      : '01';

    if (!this.ddCtrl(DirectDebitControls.AccountHolderName).value) {
      this.ddCtrl(DirectDebitControls.AccountHolderName).patchValue(name);
    }

    if (
      !this.ddCtrl(DirectDebitControls.PreferredPaymentDay).value ||
      (this.ddCtrl(DirectDebitControls.PreferredPaymentDay).value === '01' &&
        nomPayDay)
    ) {
      this.ddCtrl(DirectDebitControls.PreferredPaymentDay).patchValue(
        nomPayDay
      );
    }

    if (
      !this.ddCtrl(DirectDebitControls.AccountNumber).value ||
      this.directDebitFormState !== DirectDebitFormState.VALID
    ) {
      this.ddCtrl(DirectDebitControls.AccountNumber).patchValue(account_number);
    }

    if (
      !this.ddCtrl(DirectDebitControls.SortCode).value ||
      this.directDebitFormState !== DirectDebitFormState.VALID
    ) {
      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort1)
        .patchValue(sort_code.substring(0, 2));

      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort2)
        .patchValue(sort_code.substring(2, 4));

      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort3)
        .patchValue(sort_code.substring(4, 6));
    }

    this.setAccountValidators();
    this.setSortCodeValidators();
  }

  setAccountValidators() {
    if (this.ddCtrl(DirectDebitControls.AccountNumber).value.includes('*')) {
      this.ddCtrl(DirectDebitControls.AccountNumber).setValidators([]);
    }
  }

  setSortCodeValidators() {
    if (
      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort1)
        .value.includes('*') ||
      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort2)
        .value.includes('*')
    ) {
      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort1)
        .setValidators([]);
      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort2)
        .setValidators([]);
      this.ddCtrl(DirectDebitControls.SortCode)
        .get(DirectDebitControls.Sort3)
        .setValidators([]);
    }
  }

  /**
   * Remove the upsell before continue
   * After failed dd attempts, cta to continue to upfront payment without cover
   */
  onErrorRemoveUpsellContinueClick(step: FormStep) {
    this.resetDirectDebitState();
    this.onContinueClick(step);
  }

  /**
   * Remove the upsell before continue
   * After failed dd attempts, cta to create the order without cover
   */
  onErrorCreateOrderClick() {
    this.resetDirectDebitState();
    this.onCreateOrderClick();
  }

  /**
   * Reset direct debit state
   */
  resetDirectDebitState() {
    this.additionalServicesService.resetProtection();
    this.completeOrderService.updateDirectDebitConfig({
      reuse_dd_details: false,
    });
    this.completeOrderService.resetDirectDebitAttempts();
  }

  /**
   * Reset only sort code and account number
   */
  onFocusDirectDebitFields() {
    if (
      this.directDebitReuse &&
      this.isDirectDebitPristine &&
      !this.isFocusDirectDebitFieldsTriggered
    ) {
      this.isFocusDirectDebitFieldsTriggered = true;

      const monthlyPayment = this.ddCtrl(
        DirectDebitControls.MonthlyPayment
      ).value;
      const preferredPaymentDay = this.ddCtrl(
        DirectDebitControls.PreferredPaymentDay
      ).value;

      this.form.setControl(
        FormStep.DirectDebit,
        this.formService.directDebitForm()
      );

      this.ddCtrl(DirectDebitControls.MonthlyPayment).patchValue(
        monthlyPayment
      );
      this.ddCtrl(DirectDebitControls.PreferredPaymentDay).patchValue(
        preferredPaymentDay
      );
    }
  }

  /**
   * Emit back to parent to create order
   */
  onCreateOrderClick() {
    this.createOrderClick.emit();
  }

  /**
   * Emit back to parent to continue to next step
   */
  onContinueClick(step: FormStep) {
    this.continueClick.emit(step);
  }

  /**
   * Enter edit mode
   */
  editDirectDebit() {
    this.directDebitMode = Mode.EDIT;
    this.isFocusDirectDebitFieldsTriggered = false;
    this.isDirectDebitFormUpdated = false;
    this.previousDirectDebitState = this.form.get(FormStep.DirectDebit).value;

    if (this.directDebitReuse) {
      this.loadDirectDebitData();
    }
    this.markDirectDebitPristine();

    setTimeout(() => {
      this.scrollIntoView('accountHolder');
    }, 200);
  }

  /**
   * Exit edit mode and reset autofill details
   */
  cancelEdit() {
    this.form
      .get(FormStep.DirectDebit)
      .patchValue(this.previousDirectDebitState);
    this.setAccountValidators();
    this.setSortCodeValidators();
    this.form.get(FormStep.DirectDebit).markAllAsTouched();
    this.completeOrderService.setDirectDebitValid();
    this.directDebitMode = Mode.READONLY;
  }

  /**
   * Mark pristine
   */
  markDirectDebitPristine() {
    this.form.get(FormStep.DirectDebit).markAsPristine();
    this.ddCtrl(DirectDebitControls.AccountNumber).markAsPristine();
    this.sortCtrl(DirectDebitControls.Sort1).markAsPristine();
    this.sortCtrl(DirectDebitControls.Sort2).markAsPristine();
    this.sortCtrl(DirectDebitControls.Sort3).markAsPristine();
  }

  /**
   * Retrieve pristine state
   */
  get isDirectDebitPristine() {
    return (
      this.ddCtrl(DirectDebitControls.AccountNumber).pristine &&
      this.sortCtrl(DirectDebitControls.Sort1).pristine &&
      this.sortCtrl(DirectDebitControls.Sort2).pristine &&
      this.sortCtrl(DirectDebitControls.Sort3).pristine
    );
  }

  /**
   * Exit edit mode and save new details if changed
   */
  saveEdit() {
    if (this.form.get(FormStep.DirectDebit).valid) {
      this.isDirectDebitFormUpdated = true;
      if (!this.isDirectDebitPristine) {
        this.setupDirectDebit();
      } else {
        this.directDebitMode = Mode.READONLY;
        this.isFocusDirectDebitFieldsTriggered = false;
      }
    } else {
      this.form.get(FormStep.DirectDebit).markAllAsTouched();
      setTimeout(() => {
        this.scrollIntoView('is-invalid');
      }); // push to end of stack
    }
  }

  /**
   * get the sortCode control
   */
  sortCtrl(name?: DirectDebitControls) {
    return this.ddCtrl(DirectDebitControls.SortCode).get(name);
  }

  /**
   * Get the direct debit control
   */
  ddCtrl(name: DirectDebitControls) {
    return (this.form.get(FormStep.DirectDebit) as UntypedFormGroup).get(name);
  }

  /**
   * Handle continue CTA
   */
  onDirectDebitContinue() {
    if (!this.confirmTermsAndConditionsRead) {
      this.showReadTermsAndConditionError = true;
      return;
    }
    if (!this.directDebitAutofillAvailable) {
      this.setupDirectDebit();
    } else {
      if (this.replacementCost) {
        this.onContinueClick(FormStep.DirectDebit);
      } else {
        this.onCreateOrderClick();
      }
    }
  }

  /**
   * Vaidates the direct debit details
   */
  setupDirectDebit() {
    this.form.get(FormStep.DirectDebit).markAllAsTouched();
    if (this.form.get(FormStep.DirectDebit).valid) {
      this.completeOrderService.loadDirectDebitValidation(
        new DirectDebitHelper(
          this.currentPlan,
          this.ddCtrl(DirectDebitControls.SortCode).value,
          this.ddCtrl(DirectDebitControls.AccountNumber).value
        ).requestBody
      );
    } else {
      this.gaFormErrors(FormStep.DirectDebit);
      setTimeout(() => {
        this.scrollIntoView('is-invalid');
      }); // push to end of stack
    }
  }

  /**
   * Form validator helper for sort code
   */
  isSortInvalid(name) {
    return (
      this.sortCtrl(name).invalid &&
      (this.sortCtrl(name).touched ||
        (this.sortCtrl(name).pristine && this.sortCtrl(name).value))
    );
  }

  /**
   * Form direct debit validator helper
   */
  isValid(name) {
    return (
      this.ddCtrl(name).valid &&
      (this.ddCtrl(name).dirty || this.ddCtrl(name).pristine)
    );
  }

  /**
   * Form direct debit validator helper
   */
  isInvalid(name) {
    return (
      this.ddCtrl(name).invalid &&
      (this.ddCtrl(name).touched ||
        (this.ddCtrl(name).pristine && this.ddCtrl(name).value))
    );
  }

  /**
   * Reset the direct debit card details
   */
  resetDirectDebitCardDetails() {
    this.completeOrderService.resetDirectDebitValidation();
    this.completeOrderService.resetDirectDebitAttempts();
    this.ddCtrl(DirectDebitControls.AccountHolderName).reset();
    this.ddCtrl(DirectDebitControls.AccountNumber).reset();
    this.ddCtrl(DirectDebitControls.SortCode).reset();
    this.ddCtrl(DirectDebitControls.PreferredPaymentDay).reset(1);
  }

  /**
   * Scrolls form into view
   */
  scrollIntoView(className) {
    const elements = document.getElementsByClassName(className);
    if (elements && elements.length) {
      elements[0].scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  /**
   * Call GA form error
   */
  gaFormErrors(step: FormStep) {
    this.analyticsService.eventEmitter(
      new GACompleteOrderFormErrors(this.offers, step, this.form).gaData
    );
  }

  handleDirectDebitApiFail() {
    if (this.replacementCost) {
      this.onContinueClick(FormStep.DirectDebit);
      this.completeOrderService.resetDirectDebitValidation();
    } else {
      this.onCreateOrderClick();
    }
  }

  /**
   * Subscribe to the direct debit validation and continue to the next step if valid with no api error
   */
  validateDirectDebit() {
    this.completeOrderService.directDebitFormState$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.completeOrderService.directDebitValidationError$,
          this.completeOrderService.directDebitAttempts$
        )
      )
      .subscribe(([formState, error, attempts]) => {
        if (formState === DirectDebitFormState.ERROR) {
          this.completeOrderService.updateDirectDebitConfig({
            reuse_dd_details: false,
          });
        }

        if (
          (formState === DirectDebitFormState.VALID ||
            formState === DirectDebitFormState.ERROR) &&
          this.directDebitAutofillAvailable
        ) {
          this.directDebitMode = Mode.READONLY;
          this.previousDirectDebitState = this.form.get(
            FormStep.DirectDebit
          ).value;
        }

        if (
          formState === DirectDebitFormState.VALID &&
          !error &&
          !this.directDebitAutofillAvailable
        ) {
          if (this.replacementCost) {
            this.onContinueClick(FormStep.DirectDebit);
          } else if (attempts) {
            this.onCreateOrderClick();
          }
        }
        if (
          formState === DirectDebitFormState.INVALID ||
          formState === DirectDebitFormState.ERROR
        ) {
          setTimeout(() => {
            this.scrollIntoView('error--block');
          }); // push to end of stack
        }
      });
  }
}
